import { useMemo } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { Controller, useFormContext } from 'react-hook-form';
import { fromDate } from '@internationalized/date';
import { Key } from '@react-types/shared';
import { Checkbox, cn, Radio, RadioGroup, Tab, Tabs } from '@ds';
import { OPERATION_AT_TX_CENTER_DURATION } from '@lp/features/RoutePlanner/config';
import {
  RouteLegFormMode,
  useLegFormContext,
} from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import {
  CommonLegResourcesSchemaValues,
  GroundLegSchemaValue,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  getDefaultLegFormValues,
  getDefaultLegType,
  getDefaultValuesByLegType,
  updateRepositionCurrentValues,
} from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { useRouteLegs } from '@lp/features/RoutePlanner/hooks/useRouteLegs';
import { RandomizationResultBanner } from '@lp/features/CaseOverview/components/CaseBanners/RandomizationResultBanner';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { LegTypeEnum, RouteLegTypeEnum, SegmentTypeEnum } from '@/api/apis/route';
import { useWatchFields } from '@/hooks/useWatchFields';
import { CaseResourceType } from '@/api/apis/case';
import { getIsEuropeanRegion } from '@/utils/helpers';
import { MINUTE_IN_MS } from '@/constants/date.constants';
import { REPOSITION_SEGMENT_TYPES } from '@/utils/route';

const REPOSITION_LEG_TYPES = [
  RouteLegTypeEnum.TmdxJetAviaLeg,
  RouteLegTypeEnum.SupplementalLiftAviaLeg,
  RouteLegTypeEnum.TxCenterProviderAviaLeg,
];

export const LegTypeInputs = () => {
  const { id: logisticsId, isTmdxDriveCompatible } = useLogisticsData();
  const { mode, isNewSegment, initialSegment, calculateLegTimeDuration, previousLeg, clearErrorsAndWarnings } =
    useLegFormContext();
  const routeLegs = useRouteLegs();
  const {
    control,
    setValue,
    reset,
    getValues,
    getFieldState,
    formState: { defaultValues: formDefaultValues },
  } = useFormContext<CommonLegResourcesSchemaValues>();
  const { formType, legType, segmentType } = useWatchFields<CommonLegResourcesSchemaValues>([
    'formType',
    'legType',
    'segmentType',
  ] as const);

  const { serviceRegionId } = useCaseDetailsWithReviews();
  const isDisabledRegion = getIsEuropeanRegion(serviceRegionId);

  const isAccommodationForm = legType === RouteLegTypeEnum.AccommodationLeg;
  const isGroundForm = formType === LegTypeEnum.ground;
  const isAviaForm = formType === LegTypeEnum.air;

  const handleFormTypeChange = async (value: Key) => {
    const fieldState = getFieldState('segmentType');
    const currentValues = getValues();
    const nextLegType = getDefaultLegType({
      legs: routeLegs,
      formType: value as LegTypeEnum,
      isDisabledRegion,
      isTmdxDriveCompatible,
    });

    const defaultValues = getDefaultLegFormValues({
      currentLegType: nextLegType,
      logisticsId,
      prevLeg: previousLeg,
      isNewSegment,
      segment: initialSegment,
      legs: routeLegs,
    });

    const updatedCurrentValues = updateRepositionCurrentValues({
      currentValues,
      initialSegment,
      segmentFieldState: fieldState,
      nextLegType,
    });

    const durationData = await calculateLegTimeDuration(nextLegType);
    const newValues = getDefaultValuesByLegType({
      legType: nextLegType,
      currentValues: updatedCurrentValues,
      defaultValues,
      initialSegment,
      durationData,
    });

    reset(newValues, {
      keepDirty: true,
      keepTouched: true,
    });

    clearErrorsAndWarnings();
  };

  const updateSegmentGroupId = (newSegmentType: SegmentTypeEnum) => {
    const isSameSegmentType = initialSegment?.type === newSegmentType;
    setValue('groupId', isSameSegmentType ? initialSegment.id : uuidV4());
  };

  const handleRepositionChange = (isSelected: boolean) => {
    const currentValues = getValues();
    const isCurrentReposition = initialSegment?.type && REPOSITION_SEGMENT_TYPES.includes(initialSegment.type);
    const defaultSegmentType = isCurrentReposition
      ? SegmentTypeEnum.custom
      : initialSegment?.type || SegmentTypeEnum.custom;
    const newSegmentType = isSelected ? SegmentTypeEnum.reposition : defaultSegmentType;
    setValue('segmentType', newSegmentType);
    updateSegmentGroupId(newSegmentType);

    const isOnlyAircraftResources = currentValues.resources.every((r) =>
      [CaseResourceType.Aircraft, CaseResourceType.Crew].includes(r.resourceType),
    );

    if (isSelected && !isOnlyAircraftResources) {
      setValue(
        'resources',
        currentValues.resources.filter((r) =>
          [CaseResourceType.Aircraft, CaseResourceType.Crew].includes(r.resourceType),
        ),
      );
    }

    const startDateNow = fromDate(new Date(), 'UTC')
      .set({ second: 0, millisecond: 0 })
      .add({ milliseconds: -OPERATION_AT_TX_CENTER_DURATION })
      .toString();

    setValue('minDate', isSelected ? startDateNow : formDefaultValues?.minDate || '');
    setValue('maxDate', isSelected ? '' : formDefaultValues?.maxDate || '');
  };

  const handleReturnToBaseChange = (isSelected: boolean) => {
    const newSegmentType = isSelected ? SegmentTypeEnum.returnToBase : SegmentTypeEnum.reposition;
    setValue('segmentType', newSegmentType);
    updateSegmentGroupId(newSegmentType);
  };

  const handleLegTypeChange = async (value: string) => {
    const currentValues = getValues();
    const defaultValues = getDefaultLegFormValues({
      currentLegType: value as RouteLegTypeEnum,
      logisticsId,
      prevLeg: previousLeg,
      isNewSegment,
      segment: initialSegment,
      legs: routeLegs,
    });
    const isDeviceDelivery = currentValues.legType === RouteLegTypeEnum.CurriServiceGroundLeg;
    const isDefaultTimeDuration = currentValues.timeDurationInMs <= MINUTE_IN_MS;
    const isGroundLeg = currentValues.formType === LegTypeEnum.ground;
    const defaultDistanceInMeters = isGroundLeg ? (currentValues as GroundLegSchemaValue).distanceInMeters : 0;
    const shouldCalculateDuration =
      (isDeviceDelivery || currentValues.formType !== defaultValues.formType) && isDefaultTimeDuration;
    const durationData = shouldCalculateDuration
      ? await calculateLegTimeDuration(value as RouteLegTypeEnum)
      : { distanceInMeters: defaultDistanceInMeters, timeDurationInMs: currentValues.timeDurationInMs };
    const newValues = getDefaultValuesByLegType({
      legType: value as RouteLegTypeEnum,
      currentValues,
      defaultValues,
      durationData,
    });

    reset(newValues, {
      keepDirty: true,
      keepTouched: true,
    });

    clearErrorsAndWarnings();
  };

  const showRepositionControls = REPOSITION_LEG_TYPES.includes(legType);
  const isRepositionSegment = REPOSITION_SEGMENT_TYPES.includes(segmentType);
  const isEdit = mode === RouteLegFormMode.Edit;
  const isCommercialEnable = isNewSegment || initialSegment?.type === SegmentTypeEnum.custom;
  const hasGroundTxCenterProvider = useMemo(
    () => routeLegs.some((l) => l.legType === RouteLegTypeEnum.TxCenterProviderGroundLeg),
    [routeLegs],
  );

  const tmdxAffiliateLegType = isTmdxDriveCompatible
    ? RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg
    : RouteLegTypeEnum.TmdxAffiliateGroundLeg;
  const deviceDeliveryLegType = isTmdxDriveCompatible
    ? RouteLegTypeEnum.TmdxDriveTedsGroundLeg
    : RouteLegTypeEnum.CurriServiceGroundLeg;
  const deviceDeliveryLegName = isTmdxDriveCompatible ? 'TEDS' : 'Curri Service';

  return (
    <>
      <Controller
        name="formType"
        control={control}
        render={({ field: { value } }) => (
          <Tabs
            radius="full"
            aria-label="Select form type"
            selectedKey={value}
            onSelectionChange={handleFormTypeChange}
            isDisabled={isEdit}
            classNames={{
              tabList: 'bg-base-50 p-0 gap-0',
              tab: 'data-[disabled=true]:data-[selected=true]:opacity-80',
              tabContent: 'px-10 text-s-sb group-data-[selected=true]:text-base-90',
              cursor: cn(['bg-base-5', { 'opacity-30': isEdit }]),
            }}
          >
            <Tab className="p-0" key={LegTypeEnum.ground} title="Ground" />
            <Tab className="p-0" key={LegTypeEnum.air} title="Avia" />
            <Tab className="p-0" key={LegTypeEnum.accommodation} title="Accommodation" />
          </Tabs>
        )}
      />
      <div className="flex flex-col p-0 gap-4">
        {!isAccommodationForm && (
          <>
            <RandomizationResultBanner />
            <Controller
              name="legType"
              control={control}
              render={({ field: { value } }) => (
                <RadioGroup
                  className="flex gap-4 p-0"
                  value={value}
                  onValueChange={handleLegTypeChange}
                  orientation="horizontal"
                  isDisabled={isEdit}
                >
                  {isGroundForm && (
                    <>
                      <Radio value={tmdxAffiliateLegType}>TMDX Affiliate</Radio>
                      <Radio value={RouteLegTypeEnum.TxCenterProviderGroundLeg}>TX Center Provider</Radio>
                      <Radio value={deviceDeliveryLegType}>{deviceDeliveryLegName}</Radio>
                      <Radio value={RouteLegTypeEnum.SelfServicedGroundLeg}>Self-Serviced</Radio>
                    </>
                  )}
                  {isAviaForm && (
                    <>
                      <Radio
                        isDisabled={isEdit || hasGroundTxCenterProvider || isDisabledRegion}
                        value={RouteLegTypeEnum.TmdxJetAviaLeg}
                      >
                        TMDX Jet
                      </Radio>
                      <Radio
                        isDisabled={isEdit || !isCommercialEnable}
                        value={RouteLegTypeEnum.CommercialFlightAviaLeg}
                      >
                        Commercial Flight
                      </Radio>
                      <Radio
                        isDisabled={isEdit || hasGroundTxCenterProvider}
                        value={RouteLegTypeEnum.SupplementalLiftAviaLeg}
                      >
                        Supplemental Lift Provider
                      </Radio>
                      <Radio value={RouteLegTypeEnum.TxCenterProviderAviaLeg}>TX Center Provider</Radio>
                    </>
                  )}
                </RadioGroup>
              )}
            />
            {showRepositionControls && (
              <div className="flex gap-2">
                <Controller
                  name="segmentType"
                  control={control}
                  render={({ field: { ref, value, onChange, ...field } }) => (
                    <Checkbox
                      size="sm"
                      {...field}
                      value="isReposition"
                      isSelected={REPOSITION_SEGMENT_TYPES.includes(value)}
                      onValueChange={handleRepositionChange}
                      isDisabled={isEdit}
                      disabled={isEdit}
                    >
                      Repositioning
                    </Checkbox>
                  )}
                />
                {isRepositionSegment && (
                  <Controller
                    name="segmentType"
                    control={control}
                    render={({ field: { ref, value, onChange, ...field } }) => (
                      <Checkbox
                        size="sm"
                        {...field}
                        value="isReturnToBase"
                        isSelected={value === SegmentTypeEnum.returnToBase}
                        onValueChange={handleReturnToBaseChange}
                        isDisabled={isEdit}
                        disabled={isEdit}
                      >
                        Return to base
                      </Checkbox>
                    )}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
